/* ============================================================
   VITALCASA CRM — Contactos
   Vista unificada: Clientes · Propietarios · Otros contactos
   ============================================================ */
import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useReloadOnRouteEnter } from "../hooks/useReloadOnRouteEnter";
import { useTheme } from "../components/ThemeProvider";
import api from "../services/api";
import { dialogs } from "../context/DialogContext";
import FichaResumenPropiedad from "../components/propiedades/FichaResumenPropiedad";
import { useDraggableModalPanel } from "../hooks/useDraggableModalPanel";
import ModalWhatsApp from "../components/ModalWhatsApp";
import ModalEmail from "../components/ModalEmail";
import CitaDesdeFrioModal from "../components/CitaDesdeFrioModal";
import { useAuth } from "../context/AuthContext";
import usePermiso from "../hooks/usePermiso";
import { ROLES_CONTACTO_ASOCIADO } from "../constants/rolesContactoAsociado";
import { formatFecha } from "../utils/helpers";
import { useCaptacionRelacionContacto } from "../hooks/useCaptacionRelacionContacto";
import ModalRelacionContactoExistente from "../components/ModalRelacionContactoExistente";
import CamposDomicilioContacto from "../components/contactos/CamposDomicilioContacto";
import ModalPropiedadesQueSeAjustan from "../components/contactos/ModalPropiedadesQueSeAjustan";
import {
  domicilioContactoVacio,
  hidratarDomicilioContacto,
  rellenarDomicilioSiFalta,
  payloadDomicilioContacto,
} from "../utils/domicilioContacto";
import { provinciaNombreDesdeValor } from "../utils/provinciasEs";

// ── Columnas disponibles ───────────────────────────────────
const COLS_CLIENTES = [
  { id: "estado",         label: "Estado",      width: 90  },
  { id: "nombre",         label: "Nombre",      width: 200 },
  { id: "telefono",       label: "Teléfono",    width: 130 },
  { id: "email",          label: "Email",       width: 200 },
  { id: "tipo_operacion", label: "Operación",   width: 110 },
  { id: "agente",         label: "Agente",      width: 150 },
  { id: "fecha_alta",     label: "Fecha alta",  width: 110 },
  { id: "poblacion",      label: "Población",   width: 120 },
  { id: "provincia",      label: "Provincia",   width: 110 },
  { id: "pais",           label: "País",        width: 100 },
  { id: "dni",            label: "DNI",         width: 110 },
  { id: "idioma",         label: "Idioma",      width: 80  },
  { id: "referencias_visitadas", label: "Referencias visitadas", width: 200 },
];

function getColsClientes(bucket = "activos") {
  const b = String(bucket || "activos").toLowerCase();
  if (b !== "frios" && b !== "eliminados") return COLS_CLIENTES;
  const cols = [...COLS_CLIENTES];
  const idx = cols.findIndex((c) => c.id === "agente");
  if (idx === -1) return cols;
  cols.splice(idx + 1, 0, { id: "agente_anterior", label: "Agente anterior", width: 150 });
  return cols;
}

const COLS_PROPIETARIOS = [
  { id: "nombre",      label: "Nombre",          width: 180 },
  { id: "apellidos",   label: "Apellidos",        width: 180 },
  { id: "telefono",    label: "Teléfono",         width: 130 },
  { id: "email",       label: "Email",            width: 200 },
  { id: "propiedades", label: "Propiedades",      width: 220 },
  { id: "otros",       label: "Otros contactos",  width: 200 },
  { id: "poblacion",   label: "Población",        width: 120 },
  { id: "provincia",   label: "Provincia",        width: 110 },
  { id: "dni_nie",     label: "DNI/NIE",          width: 110 },
];

const COLS_OTROS = [
  { id: "nombre",      label: "Nombre",      width: 180 },
  { id: "apellidos",   label: "Apellidos",   width: 180 },
  { id: "telefono",    label: "Teléfono",    width: 130 },
  { id: "email",       label: "Email",       width: 200 },
  { id: "propiedades", label: "Propiedad",   width: 220 },
  { id: "propietario", label: "Propietario", width: 200 },
  { id: "rol",         label: "Rol",         width: 140 },
  { id: "poblacion",   label: "Población",   width: 120 },
  { id: "provincia",   label: "Provincia",   width: 110 },
  { id: "dni_nie",     label: "DNI/NIE",     width: 110 },
];

// ── Mapeo columna → nodo de permisos (Contactos → Clientes/Propietarios/Otros) ──
// Cada id de columna de la tabla se conecta con su nodo granular en permisosTree.js.
// Por defecto (nodo ausente o sin restricción) `puede()` devuelve true, así que sin
// tocar nada en la pantalla de Permisos el comportamiento actual no cambia.
const COL_PERM_CLIENTES = {
  estado: "ct_cli_col_estado",
  nombre: "ct_cli_col_nombre",
  telefono: "ct_cli_col_telefono",
  email: "ct_cli_col_email",
  tipo_operacion: "ct_cli_col_operacion",
  agente: "ct_cli_col_agente",
  agente_anterior: "ct_cli_col_agente_anterior",
  fecha_alta: "ct_cli_col_fecha_alta",
  poblacion: "ct_cli_col_poblacion",
  provincia: "ct_cli_col_provincia",
  pais: "ct_cli_col_pais",
  dni: "ct_cli_col_dni",
  idioma: "ct_cli_col_idioma",
  referencias_visitadas: "ct_cli_col_referencias",
};
const COL_PERM_PROPIETARIOS = {
  nombre: "ct_prop_col_nombre",
  apellidos: "ct_prop_col_apellidos",
  telefono: "ct_prop_col_telefono",
  email: "ct_prop_col_email",
  propiedades: "ct_prop_col_propiedades",
  otros: "ct_prop_col_otros",
  poblacion: "ct_prop_col_poblacion",
  provincia: "ct_prop_col_provincia",
  dni_nie: "ct_prop_col_dni",
};
const COL_PERM_OTROS = {
  nombre: "ct_otros_col_nombre",
  apellidos: "ct_otros_col_apellidos",
  telefono: "ct_otros_col_telefono",
  email: "ct_otros_col_email",
  propiedades: "ct_otros_col_propiedad",
  propietario: "ct_otros_col_propietario",
  rol: "ct_otros_col_rol",
  poblacion: "ct_otros_col_poblacion",
  provincia: "ct_otros_col_provincia",
  dni_nie: "ct_otros_col_dni",
};

// ── Mapeo campo del modal de edición → nodo de permisos (ver + modificar) ──
function nodeIdCampoEdicionContacto(key, esContactoAsociado) {
  const prefix = esContactoAsociado ? "ct_otros_edit_" : "ct_prop_edit_";
  if (key === "nombre" || key === "apellidos") return prefix + "nombre";
  if (key === "telefono" || key === "email") return prefix + "contacto";
  if (key === "dni_nie") return prefix + "dni";
  if (
    key === "poblacion" || key === "provincia" || key === "direccion"
    || key === "codigo_postal" || key === "numero" || key === "bloque"
    || key === "escalera" || key === "piso" || key === "puerta" || key === "pais"
  ) return prefix + "direccion";
  if (key === "rol") return "ct_otros_edit_rol";
  return null;
}

const STORAGE_KEYS = {
  clientes:     "vitalcasa_contactos_cols_clientes",
  clientes_frios: "vitalcasa_contactos_cols_clientes_frios",
  clientes_eliminados: "vitalcasa_contactos_cols_clientes_eliminados",
  propietarios: "vitalcasa_contactos_cols_propietarios",
  otros:        "vitalcasa_contactos_cols_otros",
};

function colsStoreKeyClientes(bucket = "activos") {
  const b = String(bucket || "activos").toLowerCase();
  if (b === "frios") return "clientes_frios";
  if (b === "eliminados") return "clientes_eliminados";
  return "clientes";
}

function cargarColsClientes(bucket, defCols) {
  const key = colsStoreKeyClientes(bucket);
  const config = cargarCols(key, defCols);
  const b = String(bucket || "activos").toLowerCase();
  if (b === "frios" || b === "eliminados") {
    return config.map((c) => (c.id === "tipo_operacion" ? { ...c, visible: true } : c));
  }
  return config;
}

function cargarCols(key, defCols) {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS[key]);
    if (saved) {
      const parsed = JSON.parse(saved);
      defCols.forEach(c => { if (!parsed.find(p => p.id === c.id)) parsed.push({ id: c.id, visible: true }); });
      return parsed;
    }
  } catch {}
  return defCols.map(c => ({ id: c.id, visible: true }));
}
function guardarCols(key, config) {
  try { localStorage.setItem(STORAGE_KEYS[key], JSON.stringify(config)); } catch {}
}

// ── Helpers ────────────────────────────────────────────────
function BadgeEstado({ estado }) {
  const m = {
    activo:     { bg: "#16a34a20", color: "#16a34a", border: "#16a34a40" },
    inactivo:   { bg: "#ef444420", color: "#ef4444", border: "#ef444440" },
    potencial:  { bg: "#f59e0b20", color: "#f59e0b", border: "#f59e0b40" },
    contactado: { bg: "#3b82f620", color: "#3b82f6", border: "#3b82f640" },
    oculto:     { bg: "#64748b20", color: "#64748b", border: "#64748b40" },
    eliminado:  { bg: "#6b728020", color: "#6b7280", border: "#6b728040" },
    ventas:     { bg: "#f472b6", color: "#000", border: "rgba(255,255,255,0.85)", borderWidth: 2 },
  };
  const key = estado?.toLowerCase();
  const c = m[key] || m.activo;
  const label = key === "ventas" ? "Ventas" : (estado || "Activo");
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        padding: "3px 10px",
        borderRadius: 20,
        background: c.bg,
        color: c.color,
        border: `${c.borderWidth || 1}px solid ${c.border}`,
      }}
    >
      {label}
    </span>
  );
}
function renderOperacionCliente(valor, t) {
  const op = String(valor || "").trim();
  if (!op) return <span style={{ color: t.textMuted }}>-</span>;
  const low = op.toLowerCase();
  const esAlquiler = low === "alquiler" || low.includes("alquiler");
  const color = esAlquiler ? "#db2777" : "#16a34a";
  const bg = esAlquiler ? "rgba(219,39,119,0.12)" : "rgba(22,163,74,0.12)";
  return (
    <span style={{ fontSize: 11, fontWeight: 700, color, background: bg, padding: "2px 7px", borderRadius: 5, whiteSpace: "nowrap" }}>
      {op}
    </span>
  );
}

// ── Configurador de columnas ───────────────────────────────
function ConfigColumnas({ storeKey, defCols, colConfig, setColConfig, t, isDark }) {
  const [abierto, setAbierto] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const toggle = (id) => {
    const nueva = colConfig.map(c => c.id === id ? { ...c, visible: !c.visible } : c);
    setColConfig(nueva);
    guardarCols(storeKey, nueva);
  };
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button onClick={() => setAbierto(!abierto)}
        style={{ padding: "7px 14px", borderRadius: 8, fontSize: 13, border: `1px solid ${t.border}`, background: t.bgCard, color: t.textSecondary, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
        ⚙️ Columnas
      </button>
      {abierto && (
        <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 200, background: isDark ? "#1e2330" : "#fff", border: `1px solid ${t.border}`, borderRadius: 10, padding: "12px 14px", width: 220, boxShadow: "0 8px 24px rgba(0,0,0,0.2)" }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", margin: "0 0 8px" }}>Columnas visibles</p>
          {defCols.map(col => {
            const cc = colConfig.find(c => c.id === col.id);
            return (
              <label key={col.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", cursor: "pointer" }}>
                <input type="checkbox" checked={cc?.visible !== false} onChange={() => toggle(col.id)} />
                <span style={{ fontSize: 13, color: t.textSecondary }}>{col.label}</span>
              </label>
            );
          })}
          <button onClick={() => setAbierto(false)} style={{ marginTop: 10, fontSize: 12, color: isDark ? "#93c5fd" : "#2563eb", background: "none", border: "none", cursor: "pointer", width: "100%", textAlign: "center" }}>Cerrar</button>
        </div>
      )}
    </div>
  );
}

// ── Modal edición contacto ─────────────────────────────────
function ModalEdicionContacto({ contacto, onClose, onGuardado, isDark, t }) {
  const esContactoAsociado = contacto.tipo === "contacto";
  const CAMPOS_BASE = [
    { key: "nombre",    label: "Nombre *",  full: false },
    { key: "apellidos", label: "Apellidos", full: false },
    { key: "telefono",  label: "Teléfono",  full: false },
    { key: "email",     label: "Email",     full: false },
    { key: "dni_nie",   label: "DNI/NIE",   full: false },
  ];
  const CAMPOS = esContactoAsociado
    ? [
        ...CAMPOS_BASE.slice(0, 4),
        { key: "rol", label: "Rol", full: true, isRol: true },
        ...CAMPOS_BASE.slice(4),
      ]
    : CAMPOS_BASE;
  const { puede } = usePermiso();
  const relacionContacto = useCaptacionRelacionContacto({
    excludeContactoId: contacto?.id || null,
    origen: "crm_contactos_edicion",
    modo: contacto?.tipo === "propietario" ? "propietario" : null,
  });
  const CAMPOS_VISIBLES = CAMPOS.filter(({ key }) => {
    const nodeId = nodeIdCampoEdicionContacto(key, esContactoAsociado);
    return !nodeId || puede(nodeId, "ver");
  });
  const [form, setForm] = useState({
    nombre:    contacto.nombre    || "",
    apellidos: contacto.apellidos || "",
    telefono:  contacto.telefono  || "",
    email:     contacto.email     || "",
    dni_nie:   contacto.dni_nie   || "",
    ...hidratarDomicilioContacto(contacto),
    provincia: provinciaNombreDesdeValor(contacto.provincia || ""),
    rol:       contacto.rol       || "",
  });
  const [guardando, setGuardando] = useState(false);
  const [guardado,  setGuardado]  = useState(false);
  const { dragRef, startDrag, panelPositionStyle } = useDraggableModalPanel();
  const [placesContactoContext, setPlacesContactoContext] = useState(null);

  useEffect(() => {
    if (!contacto?.id) return undefined;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/contactos/" + contacto.id);
        const c = data?.data;
        if (!alive || !c) return;
        setForm((f) => ({
          ...f,
          nombre: c.nombre || f.nombre,
          apellidos: c.apellidos || "",
          telefono: c.telefono || "",
          email: c.email || "",
          dni_nie: c.dni_nie || "",
          ...rellenarDomicilioSiFalta(f, c),
          provincia: provinciaNombreDesdeValor(
            rellenarDomicilioSiFalta(f, c).provincia || "",
          ),
          rol: c.rol ?? f.rol,
        }));
      } catch {
        /* datos de la fila */
      }
    })();
    return () => { alive = false; };
  }, [contacto.id]);

  useEffect(() => {
    let alive = true;
    const pob = (form.poblacion || "").trim();
    const prov = (form.provincia || "").trim();
    if (!pob) {
      setPlacesContactoContext(null);
      return undefined;
    }
    (async () => {
      try {
        const { data } = await api.get("/geo/localidad-centro", {
          params: { poblacion: pob, provincia: prov, pais: "España" },
        });
        if (!alive) return;
        if (data?.ok && data.lat != null && data.lng != null) {
          setPlacesContactoContext({ latitude: data.lat, longitude: data.lng });
        } else setPlacesContactoContext(null);
      } catch {
        if (alive) setPlacesContactoContext(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [form.poblacion, form.provincia]);

  const guardar = async () => {
    if (!form.nombre) {
      await dialogs.alert("El nombre es obligatorio");
      return;
    }
    setGuardando(true);
    try {
      await api.patch("/contactos/" + contacto.id, {
        ...form,
        ...payloadDomicilioContacto(form),
      });
      setGuardado(true);
      setTimeout(() => { onGuardado(); onClose(); }, 1200);
    } catch (e) {
      const st = e?.response?.status;
      const msg =
        st === 502 || st === 503 || st === 504
          ? `El servidor no respondió a tiempo (error ${st}). Recarga la página y comprueba si el contacto se actualizó antes de guardar otra vez.`
          : e?.response?.data?.error || e?.message || "Error al guardar";
      await dialogs.alert(msg);
    }
    finally { setGuardando(false); }
  };

  const inpSt = {
    width: "100%", padding: "8px 12px", borderRadius: 8, fontSize: 13,
    background: isDark ? "#111827" : "#f9fafb",
    border: `1.5px solid ${isDark ? "#374151" : "#e5e7eb"}`,
    color: isDark ? "#f9fafb" : "#111827",
    outline: "none", fontFamily: "inherit",
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 500, fontFamily: "'DM Sans', sans-serif" }}>
      <div ref={dragRef} onClick={e => e.stopPropagation()} style={{ width: 608, maxHeight: "92vh", display: "flex", flexDirection: "column", background: t.bgCard, borderRadius: 14, overflow: "hidden", boxShadow: "0 24px 60px rgba(0,0,0,0.4)", border: `1px solid ${t.border}`, position: "relative", ...panelPositionStyle }}>
        {guardado && (
          <div style={{ position: "absolute", inset: 0, borderRadius: 14, background: t.bgCard, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, zIndex: 2 }}>
            <div style={{ fontSize: 48 }}>✅</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#16a34a" }}>Contacto actualizado</div>
          </div>
        )}
        <div style={{ height: 3, flexShrink: 0, background: "linear-gradient(90deg, #60a5fa, #2563eb, #1d4ed8, #2563eb, #60a5fa)" }} />
        <div onMouseDown={startDrag} style={{ padding: "20px 24px 0", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, cursor: "grab", userSelect: "none" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "linear-gradient(135deg, #2563eb, #1d4ed8)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, boxShadow: "0 4px 12px rgba(37,99,235,0.35)" }}>
              {contacto.tipo === "propietario" ? "🏠" : "👤"}
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: t.textPrimary }}>Editar contacto</h2>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: t.textMuted }}>{contacto.nombre} {contacto.apellidos || ""}</p>
            </div>
          </div>
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${t.border}`, background: isDark ? "#1f2937" : "#f3f4f6", cursor: "pointer", fontSize: 16, color: t.textMuted, display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
        </div>
        <div style={{ height: 1, flexShrink: 0, background: `linear-gradient(90deg, transparent, ${t.border}, transparent)`, margin: "18px 24px 0" }} />
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px 8px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {CAMPOS_VISIBLES.map(({ key, label, full, isRol }) => {
              const nodeId = nodeIdCampoEdicionContacto(key, esContactoAsociado);
              const soloLectura = !!nodeId && !puede(nodeId, "modificar");
              const tituloSoloLectura = soloLectura ? "No tienes permiso para modificar este campo" : undefined;
              return (
              <div key={key} style={{ gridColumn: full ? "span 2" : "span 1" }} title={soloLectura ? tituloSoloLectura : undefined}>
                <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: t.textMuted, display: "block", marginBottom: 5 }}>{label}</label>
                {isRol ? (
                  <>
                    <input
                      list="roles-contacto-edicion"
                      value={form.rol}
                      onChange={(e) => setForm((f) => ({ ...f, rol: e.target.value }))}
                      style={inpSt}
                      placeholder="Ej. Hija/o, Administrador…"
                      disabled={soloLectura}
                      title={tituloSoloLectura}
                      onFocus={(e) => { e.target.style.borderColor = "#2563eb"; }}
                      onBlur={(e) => { e.target.style.borderColor = isDark ? "#374151" : "#e5e7eb"; }}
                    />
                    <datalist id="roles-contacto-edicion">
                      {ROLES_CONTACTO_ASOCIADO.map((r) => (
                        <option key={r} value={r} />
                      ))}
                    </datalist>
                  </>
                ) : (
                  <input
                    value={form[key]}
                    onChange={(e) => {
                      const valor = e.target.value;
                      setForm((f) => ({ ...f, [key]: valor }));
                      if (key === "telefono" || key === "email") {
                        relacionContacto.check(
                          key === "telefono" ? valor : form.telefono,
                          key === "email" ? valor : form.email,
                        );
                      }
                    }}
                    style={inpSt}
                    disabled={soloLectura}
                    title={tituloSoloLectura}
                    onFocus={(e) => { e.target.style.borderColor = "#2563eb"; }}
                    onBlur={(e) => { e.target.style.borderColor = isDark ? "#374151" : "#e5e7eb"; }}
                  />
                )}
              </div>
              );
            })}
            {(!nodeIdCampoEdicionContacto("direccion", esContactoAsociado) || puede(nodeIdCampoEdicionContacto("direccion", esContactoAsociado), "ver")) && (
              <CamposDomicilioContacto
                form={form}
                setForm={setForm}
                inpSt={inpSt}
                labelSt={{
                  fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em",
                  color: t.textMuted, display: "block", marginBottom: 5,
                }}
                isDark={isDark}
                disabled={!!nodeIdCampoEdicionContacto("direccion", esContactoAsociado) && !puede(nodeIdCampoEdicionContacto("direccion", esContactoAsociado), "modificar")}
                placesLocalityContext={placesContactoContext}
              />
            )}
          </div>
        </div>
        <div style={{ padding: "16px 24px 20px", display: "flex", gap: 10, justifyContent: "flex-end", flexShrink: 0, borderTop: `1px solid ${t.border}` }}>
          <button onClick={onClose} style={{ padding: "11px 24px", borderRadius: 10, border: `2px solid ${t.border}`, background: isDark ? "#1f2937" : "#f3f4f6", color: t.textMuted, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600 }}>Cancelar</button>
          <button onClick={guardar} disabled={guardando} style={{ padding: "11px 30px", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #2563eb, #1d4ed8)", color: "#fff", cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 700, boxShadow: "0 4px 14px rgba(37,99,235,0.35)", opacity: guardando ? 0.7 : 1 }}>
            {guardando ? "Guardando..." : "💾 Guardar cambios"}
          </button>
        </div>
      </div>
      <ModalRelacionContactoExistente
        open={relacionContacto.open}
        matches={relacionContacto.matches}
        canal={relacionContacto.canal}
        contexto="contacto"
        labelContinuar="Continuar de todos modos"
        labelDescartar="Cerrar aviso"
        onClose={relacionContacto.dismiss}
        onDescartar={relacionContacto.dismissDiscard}
      />
    </div>
  );
}

// ── Chip clicable de propiedad ─────────────────────────────
function ChipPropiedad({ propiedad, onClick, isDark }) {
  return (
    <span onClick={() => onClick(propiedad.id)}
      style={{ fontSize: 12, fontWeight: 600, color: isDark ? "#86efac" : "#16a34a", cursor: "pointer", textDecoration: "underline", background: isDark ? "#16a34a15" : "#f0fdf4", padding: "2px 8px", borderRadius: 6, border: `1px solid ${isDark ? "#16a34a40" : "#bbf7d0"}`, display: "inline-block", margin: "2px" }}>
      {propiedad.referencia || propiedad.direccion || "Sin ref"}
    </span>
  );
}

/** Modal al seleccionar filas en tablas de contactos (p. ej. eliminar en lote). */
function ModalAccionesSeleccion({ open, onClose, titulo, subtitulo, isDark, t, children }) {
  if (!open) return null;
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 700, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif" }}>
      <div onClick={e => e.stopPropagation()} style={{
        minWidth: 340, maxWidth: "92vw", background: t.bgCard, borderRadius: 14, border: `1px solid ${t.border}`,
        boxShadow: "0 20px 50px rgba(0,0,0,0.35)", overflow: "hidden",
      }}>
        <div style={{ padding: "16px 18px", borderBottom: `1px solid ${t.border}` }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary }}>{titulo}</div>
          {subtitulo ? <div style={{ fontSize: 12, color: t.textMuted, marginTop: 4 }}>{subtitulo}</div> : null}
        </div>
        <div style={{ padding: "16px 18px" }}>{children}</div>
        <div style={{ padding: "12px 18px", borderTop: `1px solid ${t.border}`, display: "flex", justifyContent: "flex-end" }}>
          <button type="button" onClick={onClose} style={{ padding: "8px 16px", borderRadius: 8, border: `1px solid ${t.border}`, background: isDark ? "#374151" : "#f3f4f6", color: t.textPrimary, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

// ── TABLA CLIENTES ─────────────────────────────────────────
function TablaClientes({ t, isDark, navigate, bucket = "activos", onCambiarBucket }) {
  const { usuario } = useAuth();
  const { puede } = usePermiso();
  const puedeCrearCliente = puede("ct_clientes", "alta") && puede("ct_clientes_nuevo", "ver");
  const defCols = getColsClientes(bucket);
  const [clientes, setClientes]           = useState([]);
  const [cargando, setCargando]           = useState(true);
  const [busqueda, setBusqueda]           = useState("");
  const [modalContactoOtroBucket, setModalContactoOtroBucket] = useState(null);
  const [filtroAgenteAnterior, setFiltroAgenteAnterior] = useState("");
  const [colConfig, setColConfig]         = useState(() => cargarColsClientes(bucket, defCols));
  const [seleccionados, setSeleccionados] = useState([]);
  const [modalAcciones, setModalAcciones] = useState(false);
  const [modalPropsAjustan, setModalPropsAjustan] = useState(false);
  const [loteBusy, setLoteBusy]           = useState(false);
  const [modalWA, setModalWA]            = useState(false);
  const [modalMail, setModalMail]        = useState(false);
  const [clienteCom, setClienteCom]      = useState(null);
  const [fichaPropiedad, setFichaPropiedad] = useState(null);
  const [modalCitaFrio, setModalCitaFrio] = useState(false);
  const [clienteCita, setClienteCita] = useState(null);
  const [leadCita, setLeadCita] = useState(null);
  const debounceBusquedaRef = useRef(null);
  const busquedaRef = useRef("");
  busquedaRef.current = busqueda;

  const aplicarHintContactoBusqueda = (textoBuscar, total, bucketActual, hint) => {
    const q = String(textoBuscar || "").trim();
    if (!q || !hint?.encontrado) {
      setModalContactoOtroBucket(null);
      return;
    }
    if (hint.bucket !== bucketActual) {
      setModalContactoOtroBucket({ ...hint, filtradaEnVista: false });
      return;
    }
    setModalContactoOtroBucket(null);
  };

  const cargarClientes = (texto = "") => {
    setCargando(true);
    const params = { bucket };
    const q = String(texto || "").trim();
    if (q) params.buscar = q;
    api
      .get("/clientes", { params })
      .then((r) => {
        const rows = r.data?.data || [];
        setClientes(rows);
        if (q) {
          const hint = r.data?.contacto_en_bucket;
          if (hint?.encontrado) {
            aplicarHintContactoBusqueda(q, rows.length, bucket, hint);
          } else if (rows.length === 0) {
            api.get(`/clientes/ubicacion-busqueda?q=${encodeURIComponent(q)}`)
              .then((res) => aplicarHintContactoBusqueda(q, rows.length, bucket, res.data))
              .catch(() => setModalContactoOtroBucket(null));
          } else {
            setModalContactoOtroBucket(null);
          }
        } else {
          setModalContactoOtroBucket(null);
        }
      })
      .catch(() => {
        setClientes([]);
        setModalContactoOtroBucket(null);
      })
      .finally(() => setCargando(false));
  };

  const recargar = () => cargarClientes(busqueda);

  useEffect(() => {
    setColConfig(cargarColsClientes(bucket, defCols));
  }, [bucket]);

  useReloadOnRouteEnter(() => {
    cargarClientes(busquedaRef.current);
  }, [bucket]);

  useEffect(() => {
    cargarClientes(busquedaRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucket]);

  useEffect(() => {
    return () => {
      if (debounceBusquedaRef.current) clearTimeout(debounceBusquedaRef.current);
    };
  }, []);

  useEffect(() => {
    setSeleccionados([]);
    setModalAcciones(false);
    setFiltroAgenteAnterior("");
    setModalContactoOtroBucket(null);
  }, [bucket]);

  const onCambioBusqueda = (valor) => {
    setBusqueda(valor);
    if (!String(valor || "").trim()) setModalContactoOtroBucket(null);
    if (debounceBusquedaRef.current) clearTimeout(debounceBusquedaRef.current);
    debounceBusquedaRef.current = setTimeout(() => cargarClientes(valor), 350);
  };

  const filtrados = bucket === "frios" && filtroAgenteAnterior
    ? clientes.filter((c) => c.agente_anterior_nombre === filtroAgenteAnterior)
    : clientes;

  const agentesAnteriores = bucket === "frios"
    ? [...new Set(clientes.map((c) => c.agente_anterior_nombre).filter(Boolean))].sort()
    : [];

  const toggleSeleccion = (id) => {
    setSeleccionados(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      if (prev.length === 0 && next.length > 0) setModalAcciones(true);
      return next;
    });
  };
  useEffect(() => {
    if (seleccionados.length === 0) setModalAcciones(false);
  }, [seleccionados]);

  const toggleTodos = () => {
    setSeleccionados(prev => {
      const allIds = filtrados.map(c => c.id);
      const allSelected = filtrados.length > 0 && allIds.every(id => prev.includes(id));
      let next;
      if (allSelected) {
        next = prev.filter(id => !allIds.includes(id));
      } else {
        next = [...new Set([...prev, ...allIds])];
      }
      if (prev.length === 0 && next.length > 0) setModalAcciones(true);
      return next;
    });
  };

  const enviarSeleccionAFrios = async () => {
    const ids = [...seleccionados];
    if (!ids.length) return;
    if (!(await dialogs.confirm(`¿Marcar ${ids.length} cliente(s) como frío (inactivo)? Aparecerán en la pestaña Fríos.`))) return;
    setLoteBusy(true);
    try {
      for (const id of ids) {
        await api.patch(`/clientes/${id}`, { estado: "inactivo" });
      }
      setSeleccionados([]);
      setModalAcciones(false);
      recargar();
    } catch {
      await dialogs.alert("No se pudo actualizar uno o más clientes.");
    } finally {
      setLoteBusy(false);
    }
  };

  const enviarSeleccionAEliminados = async () => {
    const ids = [...seleccionados];
    if (!ids.length) return;
    if (!(await dialogs.confirm(`¿Enviar ${ids.length} cliente(s) a Eliminados? Podrás restaurarlos desde esa pestaña.`))) return;
    setLoteBusy(true);
    try {
      for (const id of ids) {
        await api.delete(`/clientes/${id}`);
      }
      setSeleccionados([]);
      setModalAcciones(false);
      recargar();
    } catch {
      await dialogs.alert("No se pudieron archivar todos los clientes.");
    } finally {
      setLoteBusy(false);
    }
  };

  const clienteSeleccionUnico = () => {
    if (seleccionados.length !== 1) return null;
    return clientes.find(c => c.id === seleccionados[0]) || null;
  };

  const abrirWhatsApp = async () => {
    const c = clienteSeleccionUnico();
    if (!c) {
      await dialogs.alert("Selecciona un solo cliente para enviar WhatsApp.");
      return;
    }
    const tel = (c.telefono || c.whatsapp || "").trim();
    if (!tel) {
      await dialogs.alert("Este cliente no tiene teléfono ni WhatsApp.");
      return;
    }
    setClienteCom(c);
    setModalAcciones(false);
    setModalWA(true);
  };

  const abrirEmail = async () => {
    const c = clienteSeleccionUnico();
    if (!c) {
      await dialogs.alert("Selecciona un solo cliente para enviar email.");
      return;
    }
    const em = (c.email || "").trim();
    if (!em) {
      await dialogs.alert("Este cliente no tiene email.");
      return;
    }
    setClienteCom(c);
    setModalAcciones(false);
    setModalMail(true);
  };

  const abrirCitaDesdeFrios = async () => {
    const c = clienteSeleccionUnico();
    if (!c) {
      await dialogs.alert("Selecciona un solo cliente para crear la cita.");
      return;
    }
    if (!usuario?.agente_id) {
      await dialogs.alert("Inicia sesión como agente para crear la cita y reasignar el lead.");
      return;
    }
    setLoteBusy(true);
    try {
      const res = await api.get("/leads", { params: { cliente_id: c.id } });
      const leads = res.data?.data || [];
      const leadFrio =
        leads.find((l) => ["frios", "fríos"].includes(String(l.etapa || "").toLowerCase())) ||
        leads[0] ||
        null;
      if (!leadFrio) {
        await dialogs.alert("Este cliente no tiene un lead activo vinculado. No se puede continuar el flujo de visita.");
        return;
      }
      setClienteCita(c);
      setLeadCita(leadFrio);
      setModalAcciones(false);
      setModalCitaFrio(true);
    } catch {
      await dialogs.alert("No se pudo cargar el lead vinculado al cliente.");
    } finally {
      setLoteBusy(false);
    }
  };

  const onWhatsAppSendDesdeLista = async ({ propiedades, mensaje }) => {
    if (!clienteCom?.id) return;
    try {
      await api.post("/historial", {
        lead_id: null,
        cliente_id: clienteCom.id,
        tipo: "whatsapp_enviado",
        canal: "whatsapp",
        contenido: mensaje || "",
        destinatario: clienteCom.telefono || clienteCom.whatsapp || null,
        propiedades_ids: (propiedades || []).map(p => p.id).filter(Boolean).length ? (propiedades || []).map(p => p.id) : null,
        metadata: { origen: "contactos_lista" },
      });
    } catch (e) {
      console.error("[Contactos] historial WA:", e);
    }
    setClienteCom(null);
    recargar();
  };

  const onEmailSendDesdeLista = async ({ propiedades, mensaje }) => {
    if (!clienteCom?.id) return;
    try {
      await api.post("/historial", {
        lead_id: null,
        cliente_id: clienteCom.id,
        tipo: "email_enviado",
        canal: "email",
        contenido: mensaje || (propiedades || []).map(p => p.titulo).join(", ") || "",
        destinatario: clienteCom.email || null,
        propiedades_ids: (propiedades || []).map(p => p.id).filter(Boolean).length ? (propiedades || []).map(p => p.id) : null,
        metadata: { origen: "contactos_lista" },
      });
    } catch (e) {
      console.error("[Contactos] historial email:", e);
    }
    setClienteCom(null);
    recargar();
  };

  // Reactiva cliente(s) + lead vinculado desde Fríos/Eliminados y arranca el
  // flujo de contacto en la tarea indicada (T1B = llamada, T1C = WhatsApp/mail).
  const reactivarSeleccionConTarea = async (taskCode, label) => {
    const ids = [...seleccionados];
    if (!ids.length) return;
    if (!(await dialogs.confirm(
      `¿Devolver ${ids.length} cliente(s) a Activos ${label}?\n\nSe reactivará el cliente y su lead vinculado, y se creará la tarea de contacto correspondiente.`
    ))) return;
    setLoteBusy(true);
    try {
      let fallos = 0;
      for (const id of ids) {
        try {
          const { data } = await api.post(`/tareas/cliente/${id}/reactivar-con-tarea`, { taskCode });
          if (data?.ok === false) fallos++;
        } catch {
          fallos++;
        }
      }
      setSeleccionados([]);
      setModalAcciones(false);
      recargar();
      if (fallos) await dialogs.alert(`No se pudo reactivar ${fallos} de ${ids.length} cliente(s).`);
    } finally {
      setLoteBusy(false);
    }
  };
  const volverAActivosConLlamada = () => reactivarSeleccionConTarea("T1B", "con llamada");
  const volverAActivosConWhatsapp = () => reactivarSeleccionConTarea("T1C", "con WhatsApp o mail");

  const eliminarPermanentementeSeleccion = async () => {
    const ids = [...seleccionados];
    if (!ids.length || bucket !== "eliminados") return;
    const n = ids.length;
    const texto =
      `Vas a ELIMINAR DEFINITIVAMENTE ${n} cliente(s) del sistema.\n\n` +
      "• NO podrás recuperarlos desde el CRM (acción irreversible).\n" +
      "• Los leads perderán el vínculo con este cliente.\n" +
      "• Las demandas quedarán desactivadas o desvinculadas.\n" +
      "• El historial conservará entradas pero sin ficha de cliente.\n\n" +
      "¿Continuar?";
    if (!(await dialogs.confirm(texto))) return;
    if (!(await dialogs.confirm("Última confirmación: ¿borrar para siempre?"))) return;
    setLoteBusy(true);
    let errCount = 0;
    try {
      for (const id of ids) {
        try {
          await api.delete(`/clientes/${id}/permanente`);
        } catch {
          errCount++;
        }
      }
      setSeleccionados([]);
      setModalAcciones(false);
      recargar();
      if (errCount > 0) {
        await dialogs.alert(`No se pudieron eliminar ${errCount} registro(s). Puede haber restricciones en base de datos.`);
      }
    } finally {
      setLoteBusy(false);
    }
  };

  const friosDeshabilitado = bucket === "frios" || bucket === "eliminados";
  const eliminadosDeshabilitado = bucket === "eliminados";

  const visibles = defCols
    .filter(col => colConfig.find(c => c.id === col.id)?.visible !== false)
    .filter(col => puede(COL_PERM_CLIENTES[col.id] || col.id, "ver"));

  const renderCelda = (c, id) => {
    switch (id) {
      case "estado": {
        if (c.activo === false) return <BadgeEstado estado="eliminado" />;
        return <BadgeEstado estado={c.estado || "activo"} />;
      }
      case "nombre":         return <span style={{ fontWeight: 600, color: isDark ? "#93c5fd" : "#2563eb", cursor: "pointer", textDecoration: "underline" }} onClick={() => navigate("/clientes/" + c.id)}>{(c.nombre || "") + " " + (c.apellido || "")}</span>;
      case "telefono":       return <span style={{ color: isDark ? "#93c5fd" : t.accentBlue }}>{c.telefono || "-"}</span>;
      case "email":          return <span>{c.email || "-"}</span>;
      case "tipo_operacion": return renderOperacionCliente(c.tipo_operacion, t);
      case "agente":         return <span>{c.agente_nombre || "-"}</span>;
      case "agente_anterior": return <span>{c.agente_anterior_nombre || "-"}</span>;
      case "fecha_alta":     return <span>{formatFecha(c.created_at)}</span>;
      case "poblacion":      return <span>{c.poblacion || "-"}</span>;
      case "provincia":      return <span>{c.provincia || "-"}</span>;
      case "pais":           return <span>{c.pais || "-"}</span>;
      case "dni":            return <span>{c.dni || "-"}</span>;
      case "idioma":         return <span>{c.idioma || "-"}</span>;
      case "referencias_visitadas": {
        const refs = Array.isArray(c.referencias_visitadas) ? c.referencias_visitadas : [];
        if (!refs.length) return <span style={{ color: t.textMuted }}>-</span>;
        return (
          <div style={{ display: "flex", flexWrap: "wrap" }}>
            {refs.map((p) => (
              <ChipPropiedad key={p.id} propiedad={p} onClick={setFichaPropiedad} isDark={isDark} />
            ))}
          </div>
        );
      }
      default: return "-";
    }
  };

  const thSt = { padding: "9px 14px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: t.textMuted, textAlign: "left", borderBottom: `1px solid ${t.border}`, whiteSpace: "nowrap" };
  const tdSt = { padding: "8px 14px", fontSize: 13, color: t.textPrimary, borderBottom: `1px solid ${t.border}` };

  return (
    <>
      <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <input value={busqueda} onChange={e => onCambioBusqueda(e.target.value)}
            placeholder={bucket === "frios"
              ? "Buscar por nombre, email, teléfono, población, DNI, idioma o ref. visitada..."
              : "Buscar por nombre, email, teléfono, población, DNI, agente, agente anterior, idioma o ref. visitada..."}
            style={{ width: 460, padding: "8px 14px", borderRadius: 8, fontSize: 13, background: t.bgInput, border: `1px solid ${t.border}`, color: t.textPrimary, outline: "none" }} />
          {bucket === "frios" && puede("ct_clientes_filtro_agente_ant", "ver") && (
            <select
              value={filtroAgenteAnterior}
              onChange={(e) => setFiltroAgenteAnterior(e.target.value)}
              style={{ padding: "8px 12px", borderRadius: 8, fontSize: 13, background: t.bgInput, border: `1px solid ${t.border}`, color: t.textPrimary, cursor: "pointer", outline: "none", minWidth: 200 }}
            >
              <option value="">Todos los agentes anteriores</option>
              {agentesAnteriores.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          )}
          {puedeCrearCliente && (
            <button onClick={() => navigate("/clientes/nuevo")}
              style={{ padding: "8px 18px", background: t.accentBlue, color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
              + Nuevo cliente
            </button>
          )}
          {seleccionados.length > 0 && (
            <>
              <span style={{ fontSize: 13, color: t.textMuted }}>{seleccionados.length} seleccionado{seleccionados.length > 1 ? "s" : ""}</span>
              {!modalAcciones && (
                <button type="button" onClick={() => setModalAcciones(true)} style={{ padding: "6px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600, border: `1px solid ${t.border}`, background: t.bgCard, color: t.textPrimary, cursor: "pointer" }}>
                  Acciones
                </button>
              )}
            </>
          )}
        </div>
        <ConfigColumnas storeKey={colsStoreKeyClientes(bucket)} defCols={defCols} colConfig={colConfig} setColConfig={setColConfig} t={t} isDark={isDark} />
      </div>
      {cargando && !clientes.length ? <p style={{ color: t.textMuted, textAlign: "center", padding: 40 }}>Cargando...</p> : (
        <div style={{ borderRadius: 10, overflow: "auto", border: `1px solid ${t.border}`, opacity: cargando ? 0.6 : 1, transition: "opacity 0.15s" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: isDark ? t.bgPanel : "#f1f5f9" }}>
                <th style={{ ...thSt, width: 40, minWidth: 40, textAlign: "center" }}>
                  <input type="checkbox" checked={filtrados.length > 0 && filtrados.every(c => seleccionados.includes(c.id))} onChange={toggleTodos} />
                </th>
                {visibles.map(col => <th key={col.id} style={{ ...thSt, minWidth: col.width }}>{col.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {filtrados.length === 0
                ? <tr><td colSpan={visibles.length + 1} style={{ padding: 40, textAlign: "center", color: t.textMuted }}>No hay clientes</td></tr>
                : filtrados.map(c => (
                  <tr key={c.id} style={{ background: seleccionados.includes(c.id) ? (isDark ? "#1e3a5f" : "#eff6ff") : t.bgCard }}
                    onMouseEnter={e => { if (!seleccionados.includes(c.id)) e.currentTarget.style.background = isDark ? t.bgCardHover : t.accentBlueLight; }}
                    onMouseLeave={e => { e.currentTarget.style.background = seleccionados.includes(c.id) ? (isDark ? "#1e3a5f" : "#eff6ff") : t.bgCard; }}>
                    <td style={{ ...tdSt, width: 40, textAlign: "center" }} onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={seleccionados.includes(c.id)} onChange={() => toggleSeleccion(c.id)} />
                    </td>
                    {visibles.map(col => <td key={col.id} style={tdSt}>{renderCelda(c, col.id)}</td>)}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
      <ModalAccionesSeleccion
        open={modalAcciones && seleccionados.length > 0}
        onClose={() => setModalAcciones(false)}
        titulo="Acciones sobre clientes"
        subtitulo={`${seleccionados.length} fila(s) seleccionada(s).`}
        isDark={isDark}
        t={t}
      >
        <p style={{ fontSize: 12, color: t.textMuted, margin: "0 0 14px", lineHeight: 1.45 }}>
          WhatsApp, email y crear cita requieren <strong>un solo</strong> cliente seleccionado.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {(bucket === "frios" || bucket === "eliminados") && (
            <>
              {puede("ct_cli_masiva_activar_llamada", "ver") && (
                <button
                  type="button"
                  onClick={volverAActivosConLlamada}
                  disabled={loteBusy}
                  style={{
                    width: "100%", padding: "11px 14px", borderRadius: 8, border: "none",
                    background: loteBusy ? t.bgInput : "#facc15", color: loteBusy ? t.textMuted : "#000",
                    fontSize: 13, fontWeight: 600, cursor: loteBusy ? "wait" : "pointer", fontFamily: "inherit",
                  }}
                >
                  📞 Volver a Activos con llamada
                </button>
              )}
              {puede("ct_cli_masiva_activar_wa", "ver") && (
                <button
                  type="button"
                  onClick={volverAActivosConWhatsapp}
                  disabled={loteBusy}
                  style={{
                    width: "100%", padding: "11px 14px", borderRadius: 8, border: "none",
                    background: loteBusy ? t.bgInput : "#25D366", color: loteBusy ? t.textMuted : "#fff",
                    fontSize: 13, fontWeight: 600, cursor: loteBusy ? "wait" : "pointer", fontFamily: "inherit",
                  }}
                >
                  💬 Volver a Activos con WhatsApp o mail
                </button>
              )}
            </>
          )}
          {bucket === "frios" && puede("ct_cli_masiva_cita", "ver") && (
            <button
              type="button"
              onClick={abrirCitaDesdeFrios}
              disabled={loteBusy}
              style={{
                width: "100%", padding: "11px 14px", borderRadius: 8, border: "none",
                background: loteBusy ? t.bgInput : "#2563eb", color: loteBusy ? t.textMuted : "#fff",
                fontSize: 13, fontWeight: 600, cursor: loteBusy ? "wait" : "pointer", fontFamily: "inherit",
              }}
            >
              📅 Crear cita
            </button>
          )}
          {puede("ct_cli_masiva_whatsapp", "ver") && (
            <button
              type="button"
              onClick={abrirWhatsApp}
              disabled={loteBusy}
              style={{
                width: "100%", padding: "11px 14px", borderRadius: 8, border: "none",
                background: "linear-gradient(135deg, #15803d, #16a34a)", color: "#fff",
                fontSize: 13, fontWeight: 600, cursor: loteBusy ? "wait" : "pointer", fontFamily: "inherit",
              }}
            >
              💬 Enviar WhatsApp
            </button>
          )}
          {puede("ct_cli_masiva_email", "ver") && (
            <button
              type="button"
              onClick={abrirEmail}
              disabled={loteBusy}
              style={{
                width: "100%", padding: "11px 14px", borderRadius: 8, border: "none",
                background: "linear-gradient(135deg, #b45309, #d97706)", color: "#fff",
                fontSize: 13, fontWeight: 600, cursor: loteBusy ? "wait" : "pointer", fontFamily: "inherit",
              }}
            >
              ✉️ Enviar mail
            </button>
          )}
          {puede("ct_cli_masiva_frios", "ver") && (
            <button
              type="button"
              onClick={enviarSeleccionAFrios}
              disabled={loteBusy || friosDeshabilitado}
              title={friosDeshabilitado ? "No aplica en esta vista" : ""}
              style={{
                width: "100%", padding: "11px 14px", borderRadius: 8, border: `1px solid ${isDark ? "#3b82f680" : "#93c5fd"}`,
                background: isDark ? "rgba(59,130,246,0.15)" : "#eff6ff", color: isDark ? "#93c5fd" : "#1d4ed8",
                fontSize: 13, fontWeight: 600, cursor: loteBusy || friosDeshabilitado ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: friosDeshabilitado ? 0.45 : 1,
              }}
            >
              ❄️ Enviar a Fríos
            </button>
          )}
          {puede("ct_cli_masiva_eliminados", "ver") && (
            <button
              type="button"
              onClick={enviarSeleccionAEliminados}
              disabled={loteBusy || eliminadosDeshabilitado}
              title={eliminadosDeshabilitado ? "Ya están en eliminados" : ""}
              style={{
                width: "100%", padding: "11px 14px", borderRadius: 8, border: "none",
                background: eliminadosDeshabilitado ? t.bgInput : "#dc2626", color: eliminadosDeshabilitado ? t.textMuted : "#fff",
                fontSize: 13, fontWeight: 600, cursor: loteBusy || eliminadosDeshabilitado ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: eliminadosDeshabilitado ? 0.45 : 1,
              }}
            >
              🗑️ Enviar a Eliminados
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setModalAcciones(false);
              setModalPropsAjustan(true);
            }}
            disabled={loteBusy || seleccionados.length === 0}
            style={{
              width: "100%", padding: "11px 14px", borderRadius: 8, border: "none",
              background: "linear-gradient(135deg, #0f766e, #14b8a6)", color: "#fff",
              fontSize: 13, fontWeight: 600, cursor: loteBusy ? "wait" : "pointer", fontFamily: "inherit",
            }}
          >
            🏠 Ver propiedades que se ajustan
          </button>
          {bucket === "eliminados" && puede("ct_cli_masiva_eliminar_perm", "ver") && (
            <button
              type="button"
              onClick={eliminarPermanentementeSeleccion}
              disabled={loteBusy}
              style={{
                width: "100%", padding: "11px 14px", borderRadius: 8, border: "2px solid #b91c1c",
                background: isDark ? "#450a0a" : "#fef2f2", color: "#b91c1c",
                fontSize: 13, fontWeight: 700, cursor: loteBusy ? "wait" : "pointer", fontFamily: "inherit",
              }}
            >
              ⛔ Eliminar permanentemente
            </button>
          )}
        </div>
      </ModalAccionesSeleccion>

      {modalWA && clienteCom && (
        <ModalWhatsApp
          isOpen={modalWA}
          onClose={() => { setModalWA(false); setClienteCom(null); }}
          onSend={onWhatsAppSendDesdeLista}
          idioma={clienteCom.idioma || "ES"}
          crmContext={{ cliente_id: clienteCom.id, lead_id: null }}
          contact={{
            nombre: `${clienteCom.nombre || ""} ${clienteCom.apellido || ""}`.trim() || "Cliente",
            telefono: clienteCom.telefono || clienteCom.whatsapp || "",
            iniciales: `${(clienteCom.nombre || "?").charAt(0)}${(clienteCom.apellido || "").charAt(0) || ""}`.toUpperCase(),
          }}
          properties={[]}
        />
      )}
      {modalMail && clienteCom && (
        <ModalEmail
          isOpen={modalMail}
          onClose={() => { setModalMail(false); setClienteCom(null); }}
          onSend={onEmailSendDesdeLista}
          crmContext={{ cliente_id: clienteCom.id, lead_id: null }}
          contact={{
            nombre: `${clienteCom.nombre || ""} ${clienteCom.apellido || ""}`.trim() || "Cliente",
            email: clienteCom.email || "",
            iniciales: `${(clienteCom.nombre || "?").charAt(0)}${(clienteCom.apellido || "").charAt(0) || ""}`.toUpperCase(),
          }}
          properties={[]}
        />
      )}
      {modalCitaFrio && clienteCita && leadCita && (
        <CitaDesdeFrioModal
          open={modalCitaFrio}
          lead={leadCita}
          cliente={clienteCita}
          usuario={usuario}
          agentName={usuario?.nombre || ""}
          onClose={() => { setModalCitaFrio(false); setClienteCita(null); setLeadCita(null); }}
          onSuccess={() => {
            setSeleccionados([]);
            setModalCitaFrio(false);
            setClienteCita(null);
            setLeadCita(null);
            recargar();
          }}
        />
      )}
      {fichaPropiedad && (
        <FichaResumenPropiedad propiedadId={fichaPropiedad} onClose={() => setFichaPropiedad(null)} />
      )}

      <ModalPropiedadesQueSeAjustan
        open={modalPropsAjustan && seleccionados.length > 0}
        onClose={() => setModalPropsAjustan(false)}
        clientes={clientes.filter((c) => seleccionados.includes(c.id))}
        t={t}
        isDark={isDark}
        onAbrirFicha={(id) => setFichaPropiedad(id)}
      />

      {modalContactoOtroBucket && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, background: "rgba(0,0,0,0.55)" }}
          onClick={() => setModalContactoOtroBucket(null)}
        >
          <div
            role="dialog"
            aria-labelledby="modal-contacto-bucket-titulo"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(420px, 100%)",
              background: t.bgCard,
              border: `1px solid ${t.border}`,
              borderRadius: 12,
              padding: "22px 24px",
              boxShadow: "0 20px 50px rgba(0,0,0,0.35)",
            }}
          >
            <h3
              id="modal-contacto-bucket-titulo"
              style={{ margin: "0 0 10px", fontSize: 16, fontWeight: 700, color: t.textPrimary }}
            >
              Contacto en otra pestaña
            </h3>
            <p style={{ margin: "0 0 6px", fontSize: 14, color: t.textSecondary, lineHeight: 1.5 }}>
              El contacto{" "}
              <strong style={{ color: t.textPrimary }}>{modalContactoOtroBucket.nombre}</strong>{" "}
              se encuentra en{" "}
              <strong style={{ color: t.textPrimary }}>{modalContactoOtroBucket.etiqueta}</strong>.
            </p>
            <p style={{ margin: "0 0 18px", fontSize: 12, color: t.textMuted }}>
              No aparece en la vista actual ({BUCKET_CLIENTES_LABELS[bucket] || bucket}).
              El buscador consulta Activos, Ocultos, Fríos y Eliminados.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => setModalContactoOtroBucket(null)}
                style={{
                  padding: "8px 14px", borderRadius: 8, border: `1px solid ${t.border}`,
                  background: "transparent", color: t.textSecondary, fontSize: 13, cursor: "pointer",
                }}
              >
                Cerrar
              </button>
              {onCambiarBucket && (
                <button
                  type="button"
                  onClick={() => {
                    onCambiarBucket(modalContactoOtroBucket.bucket);
                    setModalContactoOtroBucket(null);
                  }}
                  style={{
                    padding: "8px 14px", borderRadius: 8, border: "none",
                    background: t.accentBlue, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer",
                  }}
                >
                  Ir a {modalContactoOtroBucket.etiqueta}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const BUCKET_CLIENTES_LABELS = {
  activos: "Activos",
  ocultos: "Ocultos",
  frios: "Fríos",
  eliminados: "Eliminados",
  ventas: "Ventas",
};

// ── TABLA PROPIETARIOS / OTROS ─────────────────────────────
function TablaContactos({ tipo, t, isDark }) {
  const { puede } = usePermiso();
  const nodeIdContacto = tipo === "propietario" ? "ct_propietarios" : "ct_otros";
  const nodeIdEliminarContacto = tipo === "propietario" ? "ct_propietarios_eliminar" : "ct_otros_eliminar";
  const nodeIdNuevoContacto = tipo === "propietario" ? "ct_propietarios_nuevo" : "ct_otros_nuevo";
  const colPermMap = tipo === "propietario" ? COL_PERM_PROPIETARIOS : COL_PERM_OTROS;
  const puedeCrearContacto = puede(nodeIdContacto, "alta") && puede(nodeIdNuevoContacto, "ver");
  // "baja" (ruta backend, ya wired) + el nodo granular "Eliminar (fila y masivo)": ambos deben
  // permitirlo para mostrar el botón de eliminar (fila individual y acción masiva).
  const puedeEliminarContacto = puede(nodeIdContacto, "baja") && puede(nodeIdEliminarContacto, "ver");
  const storeKey = tipo === "propietario" ? "propietarios" : "otros";
  const defCols  = tipo === "propietario" ? COLS_PROPIETARIOS : COLS_OTROS;

  const [contactos, setContactos]         = useState([]);
  const [cargando, setCargando]           = useState(true);
  const [busqueda, setBusqueda]           = useState("");
  const [seleccionados, setSeleccionados] = useState([]);
  const [modalAcciones, setModalAcciones] = useState(false);
  const [eliminandoLote, setEliminandoLote] = useState(false);
  const debounceRef = useRef(null);
  const busquedaRef = useRef("");
  busquedaRef.current = busqueda;

  const toggleSeleccion = (id) => {
    setSeleccionados(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      if (prev.length === 0 && next.length > 0) setModalAcciones(true);
      return next;
    });
  };
  useEffect(() => {
    if (seleccionados.length === 0) setModalAcciones(false);
  }, [seleccionados]);
  const toggleTodos = () => {
    setSeleccionados(prev => {
      const allIds = filtrados.map(c => c.id);
      const allSelected = filtrados.length > 0 && allIds.every(id => prev.includes(id));
      let next;
      if (allSelected) {
        next = prev.filter(id => !allIds.includes(id));
      } else {
        next = [...new Set([...prev, ...allIds])];
      }
      if (prev.length === 0 && next.length > 0) setModalAcciones(true);
      return next;
    });
  };
  const [colConfig, setColConfig]         = useState(() => cargarCols(storeKey, defCols));
  const [modalNuevo, setModalNuevo]       = useState(false);
  const [modalEditar, setModalEditar]     = useState(null);
  const [fichaPropiedad, setFichaPropiedad] = useState(null); // ID propiedad para panel lateral
  const [form, setForm]                   = useState({ nombre: "", apellidos: "", telefono: "", email: "", dni_nie: "", ...domicilioContactoVacio() });
  const [guardando, setGuardando]         = useState(false);
  const [placesContactoNuevoContext, setPlacesContactoNuevoContext] = useState(null);
  const nuevoModalDrag = useDraggableModalPanel();
  const relacionContactoNuevo = useCaptacionRelacionContacto({
    origen: tipo === "propietario" ? "crm_contactos_nuevo_propietario" : "crm_contactos_nuevo_otro",
    modo: tipo === "propietario" ? "propietario" : null,
  });

  useEffect(() => {
    if (!modalNuevo) {
      relacionContactoNuevo.resetSeen();
      relacionContactoNuevo.closeQuiet();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalNuevo]);

  useEffect(() => {
    if (!modalNuevo) {
      setPlacesContactoNuevoContext(null);
      return undefined;
    }
    let alive = true;
    const pob = (form.poblacion || "").trim();
    const prov = (form.provincia || "").trim();
    if (!pob) {
      setPlacesContactoNuevoContext(null);
      return undefined;
    }
    (async () => {
      try {
        const { data } = await api.get("/geo/localidad-centro", {
          params: { poblacion: pob, provincia: prov, pais: "España" },
        });
        if (!alive) return;
        if (data?.ok && data.lat != null && data.lng != null) {
          setPlacesContactoNuevoContext({ latitude: data.lat, longitude: data.lng });
        } else setPlacesContactoNuevoContext(null);
      } catch {
        if (alive) setPlacesContactoNuevoContext(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [modalNuevo, form.poblacion, form.provincia]);

  const cargar = (texto = "") => {
    const params = texto ? `?tipo=${tipo}&buscar=${encodeURIComponent(texto)}` : `?tipo=${tipo}`;
    api
      .get(`/contactos${params}`)
      .then((r) => setContactos(r.data.data || []))
      .catch((err) => {
        console.error("Error al buscar contactos:", err?.response?.data?.error || err.message);
        setContactos([]);
      })
      .finally(() => setCargando(false));
  };

  useReloadOnRouteEnter(() => {
    setCargando(true);
    cargar(busquedaRef.current);
  }, [tipo]);

  const onCambioBusqueda = (valor) => {
    setBusqueda(valor);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => cargar(valor), 350);
  };

  const filtrados = contactos;

  const visibles = defCols
    .filter(col => colConfig.find(c => c.id === col.id)?.visible !== false)
    .filter(col => puede(colPermMap[col.id] || col.id, "ver"));

  const guardar = async () => {
    if (!form.nombre) {
      await dialogs.alert("El nombre es obligatorio");
      return;
    }
    setGuardando(true);
    try {
      await api.post("/contactos", { ...form, ...payloadDomicilioContacto(form), tipo });
      setModalNuevo(false);
      setForm({ nombre: "", apellidos: "", telefono: "", email: "", dni_nie: "", ...domicilioContactoVacio() });
      cargar();
    } catch { await dialogs.alert("Error al guardar"); } finally { setGuardando(false); }
  };

  const eliminar = async (id) => {
    if (!(await dialogs.confirm("¿Eliminar este contacto?"))) return;
    try { await api.delete("/contactos/" + id); cargar(busqueda); } catch { await dialogs.alert("Error al eliminar"); }
  };

  const eliminarSeleccionados = async () => {
    const ids = [...seleccionados];
    if (!ids.length) return;
    if (!(await dialogs.confirm(`¿Eliminar ${ids.length} contacto(s)? Esta acción no se puede deshacer.`))) return;
    setEliminandoLote(true);
    try {
      for (const id of ids) {
        await api.delete("/contactos/" + id);
      }
      setSeleccionados([]);
      setModalAcciones(false);
      cargar(busqueda);
    } catch {
      await dialogs.alert("No se pudieron eliminar todos los contactos.");
    } finally {
      setEliminandoLote(false);
    }
  };

  const getPropiedades = (c) => {
    if (!c.propiedades_arr) return [];
    if (Array.isArray(c.propiedades_arr)) return c.propiedades_arr.filter(Boolean);
    try { return JSON.parse(c.propiedades_arr).filter(Boolean); } catch { return []; }
  };

  const renderCelda = (c, id) => {
    switch (id) {
      case "nombre":
        return <span onClick={() => setModalEditar(c)} style={{ fontWeight: 600, color: isDark ? "#93c5fd" : "#2563eb", cursor: "pointer", textDecoration: "underline" }}>{c.nombre}</span>;
      case "apellidos": return <span style={{ color: t.textPrimary }}>{c.apellidos || "-"}</span>;
      case "telefono":  return <span style={{ color: isDark ? "#93c5fd" : t.accentBlue }}>{c.telefono || "-"}</span>;
      case "email":     return <span style={{ color: t.textPrimary }}>{c.email || "-"}</span>;
      case "rol":
        if (tipo !== "contacto") return <span style={{ color: t.textMuted }}>-</span>;
        return <span style={{ color: t.textPrimary }}>{c.rol || "-"}</span>;
      case "poblacion": return <span style={{ color: t.textPrimary }}>{c.poblacion || "-"}</span>;
      case "provincia": return <span style={{ color: t.textPrimary }}>{c.provincia || "-"}</span>;
      case "dni_nie":   return <span style={{ color: t.textPrimary }}>{c.dni_nie || "-"}</span>;

      case "propiedades": {
        const props = getPropiedades(c);
        if (!props.length) return <span style={{ color: t.textMuted }}>-</span>;
        return (
          <div style={{ display: "flex", flexWrap: "wrap" }}>
            {props.map(p => <ChipPropiedad key={p.id} propiedad={p} onClick={setFichaPropiedad} isDark={isDark} />)}
          </div>
        );
      }

      case "otros": {
        // Para propietarios: mostrar otros contactos vinculados a sus propiedades
        const props = getPropiedades(c);
        if (!props.length) return <span style={{ color: t.textMuted }}>-</span>;
        const propIds = props.map(p => p.id).filter(Boolean);
        return <OtrosContactosProp propiedadIds={propIds} onEditar={setModalEditar} isDark={isDark} t={t} />;
      }

      case "propietario": {
        const props = getPropiedades(c);
        if (!props.length) return <span style={{ color: t.textMuted }}>-</span>;
        const propietarios = [...new Map(props.filter(p => p.propietario_id).map(p => [p.propietario_id, p])).values()];
        if (!propietarios.length) return <span style={{ color: t.textMuted }}>-</span>;
        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {propietarios.map(p => (
              <span key={p.propietario_id}
                onClick={() => setModalEditar({ id: p.propietario_id, nombre: (p.propietario_nombre || "").trim().split(" ")[0] || "", apellidos: (p.propietario_nombre || "").trim().split(" ").slice(1).join(" "), tipo: "propietario" })}
                style={{ fontSize: 12, fontWeight: 600, color: isDark ? "#93c5fd" : "#2563eb", cursor: "pointer", textDecoration: "underline" }}>
                {(p.propietario_nombre || "").trim() || "-"}
              </span>
            ))}
          </div>
        );
      }

      default: return "-";
    }
  };

  const thSt  = { padding: "9px 14px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: t.textMuted, textAlign: "left", borderBottom: `1px solid ${t.border}`, whiteSpace: "nowrap" };
  const tdSt  = { padding: "8px 14px", fontSize: 13, color: t.textPrimary, borderBottom: `1px solid ${t.border}` };
  const inpSt = { width: "100%", padding: "7px 10px", borderRadius: 7, fontSize: 13, background: isDark ? "#1f2937" : "#fff", border: `1px solid ${isDark ? "#374151" : "#d1d5db"}`, color: isDark ? "#f9fafb" : "#111827", outline: "none", fontFamily: "inherit" };

  return (
    <>
      <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <input value={busqueda}
            onChange={e => onCambioBusqueda(e.target.value)}
            placeholder={tipo === "propietario"
              ? "Buscar por nombre, email, teléfono, DNI, población o ref. propiedad..."
              : "Buscar por nombre, email, teléfono, DNI, población..."}
            style={{ width: 360, padding: "8px 14px", borderRadius: 8, fontSize: 13, background: t.bgInput, border: `1px solid ${t.border}`, color: t.textPrimary, outline: "none" }} />
          {puedeCrearContacto && (
            <button onClick={() => setModalNuevo(true)}
              style={{ padding: "8px 18px", background: t.accentBlue, color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
              + Nuevo {tipo === "propietario" ? "propietario" : "contacto"}
            </button>
          )}
          {seleccionados.length > 0 && (
            <>
              <span style={{ fontSize: 13, color: t.textMuted }}>{seleccionados.length} seleccionado{seleccionados.length > 1 ? "s" : ""}</span>
              {!modalAcciones && (
                <button type="button" onClick={() => setModalAcciones(true)} style={{ padding: "6px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600, border: `1px solid ${t.border}`, background: t.bgCard, color: t.textPrimary, cursor: "pointer" }}>
                  Acciones
                </button>
              )}
            </>
          )}
        </div>
        <ConfigColumnas storeKey={storeKey} defCols={defCols} colConfig={colConfig} setColConfig={setColConfig} t={t} isDark={isDark} />
      </div>

      {cargando && !contactos.length ? <p style={{ color: t.textMuted, textAlign: "center", padding: 40 }}>Cargando...</p> : (
        <div style={{ borderRadius: 10, overflow: "auto", border: `1px solid ${t.border}`, opacity: cargando ? 0.6 : 1, transition: "opacity 0.15s" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: isDark ? t.bgPanel : "#f1f5f9" }}>
                <th style={{ ...thSt, width: 40, minWidth: 40, textAlign: "center" }}>
                  <input type="checkbox" checked={filtrados.length > 0 && filtrados.every(c => seleccionados.includes(c.id))} onChange={toggleTodos} />
                </th>
                {visibles.map(col => <th key={col.id} style={{ ...thSt, minWidth: col.width }}>{col.label}</th>)}
                <th style={{ ...thSt, width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtrados.length === 0
                ? <tr><td colSpan={visibles.length + 2} style={{ padding: 40, textAlign: "center", color: t.textMuted }}>No hay {tipo === "propietario" ? "propietarios" : "contactos"} registrados</td></tr>
                : filtrados.map(c => (
                  <tr key={c.id} style={{ background: seleccionados.includes(c.id) ? (isDark ? "#1e3a5f" : "#eff6ff") : t.bgCard }}
                    onMouseEnter={e => { if (!seleccionados.includes(c.id)) e.currentTarget.style.background = isDark ? t.bgCardHover : t.accentBlueLight; }}
                    onMouseLeave={e => { e.currentTarget.style.background = seleccionados.includes(c.id) ? (isDark ? "#1e3a5f" : "#eff6ff") : t.bgCard; }}>
                    <td style={{ ...tdSt, width: 40, textAlign: "center" }} onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={seleccionados.includes(c.id)} onChange={() => toggleSeleccion(c.id)} />
                    </td>
                    {visibles.map(col => <td key={col.id} style={tdSt}>{renderCelda(c, col.id)}</td>)}
                    <td style={{ ...tdSt, textAlign: "right" }}>
                      {puedeEliminarContacto && (
                        <button onClick={() => eliminar(c.id)} style={{ fontSize: 12, color: "#ef4444", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Eliminar</button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal nuevo */}
      {modalNuevo && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 500 }}>
          <div
            ref={nuevoModalDrag.dragRef}
            style={{
              background: isDark ? "#1e2330" : "#fff",
              borderRadius: 14,
              padding: 0,
              width: 608,
              maxWidth: "94vw",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              border: `1px solid ${isDark ? "#374151" : "#e5e7eb"}`,
              overflow: "hidden",
              ...nuevoModalDrag.panelPositionStyle,
            }}
          >
            <div
              onMouseDown={nuevoModalDrag.startDrag}
              style={{
                padding: "18px 28px 12px",
                cursor: "grab",
                userSelect: "none",
                borderBottom: `1px solid ${isDark ? "#374151" : "#e5e7eb"}`,
              }}
            >
              <h3 style={{ fontSize: 15, fontWeight: 700, color: isDark ? "#f3f4f6" : "#111827", margin: 0 }}>Nuevo {tipo === "propietario" ? "propietario" : "contacto"}</h3>
            </div>
            <div style={{ padding: "16px 28px 28px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {[
                { key: "nombre",    label: "Nombre *",  full: false },
                { key: "apellidos", label: "Apellidos", full: false },
                { key: "telefono",  label: "Teléfono",  full: false },
                { key: "email",     label: "Email",     full: false },
                { key: "dni_nie",   label: "DNI/NIE",   full: false },
              ].map(({ key, label, full }) => (
                <div key={key} style={{ gridColumn: full ? "span 2" : "span 1" }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: isDark ? "#9ca3af" : "#6b7280", display: "block", marginBottom: 4 }}>{label}</label>
                  <input
                    value={form[key]}
                    onChange={(e) => {
                      const valor = e.target.value;
                      setForm((f) => ({ ...f, [key]: valor }));
                      if (key === "telefono" || key === "email") {
                        relacionContactoNuevo.check(
                          key === "telefono" ? valor : form.telefono,
                          key === "email" ? valor : form.email,
                        );
                      }
                    }}
                    style={inpSt}
                    type={key === "email" ? "email" : key === "telefono" ? "tel" : "text"}
                  />
                </div>
              ))}
              <CamposDomicilioContacto
                form={form}
                setForm={setForm}
                inpSt={inpSt}
                labelSt={{ fontSize: 11, fontWeight: 600, color: isDark ? "#9ca3af" : "#6b7280", display: "block", marginBottom: 4 }}
                isDark={isDark}
                placesLocalityContext={placesContactoNuevoContext}
              />
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
              <button onClick={() => setModalNuevo(false)} style={{ padding: "8px 18px", border: `1px solid ${isDark ? "#374151" : "#d1d5db"}`, borderRadius: 8, background: "transparent", color: isDark ? "#f3f4f6" : "#374151", fontSize: 13, cursor: "pointer" }}>Cancelar</button>
              <button onClick={guardar} disabled={guardando} style={{ padding: "8px 20px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: guardando ? 0.6 : 1 }}>{guardando ? "Guardando..." : "Guardar"}</button>
            </div>
            </div>
          </div>
          <ModalRelacionContactoExistente
            open={relacionContactoNuevo.open}
            matches={relacionContactoNuevo.matches}
            canal={relacionContactoNuevo.canal}
            contexto="contacto"
            onClose={relacionContactoNuevo.dismiss}
            onDescartar={() => {
              relacionContactoNuevo.dismissDiscard();
              setModalNuevo(false);
              setForm({ nombre: "", apellidos: "", telefono: "", email: "", dni_nie: "", ...domicilioContactoVacio() });
            }}
          />
        </div>
      )}

      {/* Modal edición */}
      {modalEditar && (
        <ModalEdicionContacto contacto={modalEditar} onClose={() => setModalEditar(null)} onGuardado={cargar} isDark={isDark} t={t} />
      )}

      <ModalAccionesSeleccion
        open={modalAcciones && seleccionados.length > 0}
        onClose={() => setModalAcciones(false)}
        titulo={tipo === "propietario" ? "Acciones sobre propietarios" : "Acciones sobre contactos"}
        subtitulo={`${seleccionados.length} fila(s) seleccionada(s).`}
        isDark={isDark}
        t={t}
      >
        <p style={{ fontSize: 13, color: t.textSecondary, margin: "0 0 14px", lineHeight: 1.45 }}>
          Eliminar los registros seleccionados. Si hay datos vinculados en propiedades, el borrado puede fallar.
        </p>
        {puedeEliminarContacto && (
          <button
            type="button"
            onClick={eliminarSeleccionados}
            disabled={eliminandoLote}
            style={{
              width: "100%", padding: "10px 14px", borderRadius: 8, border: "none", fontSize: 13, fontWeight: 600,
              background: "#dc2626", color: "#fff", cursor: eliminandoLote ? "wait" : "pointer", opacity: eliminandoLote ? 0.75 : 1,
            }}
          >
            {eliminandoLote ? "Eliminando…" : "Eliminar selección"}
          </button>
        )}
      </ModalAccionesSeleccion>

      {/* Panel lateral ficha propiedad — sin salir de la página */}
      {fichaPropiedad && (
        <FichaResumenPropiedad propiedadId={fichaPropiedad} onClose={() => setFichaPropiedad(null)} />
      )}
    </>
  );
}

// ── Mini-componente: otros contactos de una propiedad ──────
function OtrosContactosProp({ propiedadIds, onEditar, isDark, t }) {
  const [otros, setOtros] = useState([]);
  useEffect(() => {
    if (!propiedadIds.length) return;
    // Cargamos los contactos de tipo "contacto" y filtramos los que pertenecen a estas propiedades
    Promise.all(propiedadIds.map(id => api.get(`/propiedades/${id}/contactos`).catch(() => ({ data: { data: [] } }))))
      .then(results => {
        const todos = results.flatMap(r => r.data.data || []);
        const uniq = [...new Map(todos.map(c => [c.id, c])).values()];
        setOtros(uniq);
      });
  }, [propiedadIds.join(",")]);

  if (!otros.length) return <span style={{ color: t.textMuted }}>-</span>;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
      {otros.map(c => (
        <span key={c.id} onClick={() => onEditar(c)}
          style={{ fontSize: 12, fontWeight: 600, color: isDark ? "#93c5fd" : "#2563eb", cursor: "pointer", textDecoration: "underline" }}>
          {c.nombre} {c.apellidos || ""}
        </span>
      ))}
    </div>
  );
}

// ── COMPONENTE PRINCIPAL ───────────────────────────────────
export default function Contactos() {
  const { theme } = useTheme();
  const navigate  = useNavigate();
  const t = theme.colors;
  const isDark = theme.name === "dark";
  const { puede } = usePermiso();
  const [vista, setVista] = useState("clientes");
  const [bucketClientes, setBucketClientes] = useState("activos");

  const BUCKET_CLIENTES = [
    { id: "activos", label: "Activos" },
    { id: "ocultos", label: "Ocultos" },
    { id: "frios", label: "Fríos" },
    { id: "eliminados", label: "Eliminados" },
    { id: "ventas", label: "Ventas" },
  ];
  // Cada pestaña de Clientes tiene su propio permiso (ct_clientes_<id>): un rol/usuario
  // puede tener, p. ej., Activos y Ventas pero no Fríos.
  const bucketsClientesVisibles = useMemo(
    () => BUCKET_CLIENTES.filter((b) => puede(`ct_clientes_${b.id}`, "ver")),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [puede]
  );

  // Si la pestaña activa (por defecto "activos") no está permitida para este
  // rol/usuario, saltar a la primera pestaña de Clientes que sí lo esté.
  useEffect(() => {
    if (bucketsClientesVisibles.length === 0) return;
    if (bucketsClientesVisibles.some((b) => b.id === bucketClientes)) return;
    setBucketClientes(bucketsClientesVisibles[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucketsClientesVisibles]);

  const BOTONES = [
    { id: "clientes",     label: "👤 Clientes" },
    { id: "propietarios", label: "🏠 Propietarios" },
    { id: "otros",        label: "📋 Otros contactos" },
  ];

  return (
    <div style={{ minHeight: "100vh", background: t.bgApp }}>
      <header style={{ background: t.bgCard, borderBottom: `1px solid ${t.border}`, padding: "14px 24px", display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 700, color: t.textPrimary, margin: 0 }}>Contactos</h1>
          <p style={{ fontSize: 12, color: t.textMuted, margin: "2px 0 0" }}>Gestión de clientes, propietarios y otros contactos</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {BOTONES.map(b => {
            const activo = vista === b.id;
            return (
              <button key={b.id} onClick={() => setVista(b.id)}
                style={{ padding: "8px 20px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all 0.15s", border: `1px solid ${activo ? t.accentBlue : t.border}`, background: activo ? t.accentBlue : t.bgCard, color: activo ? "#fff" : t.textSecondary }}>
                {b.label}
              </button>
            );
          })}
        </div>
        <div />
      </header>
      <main style={{ padding: 20 }}>
        {vista === "clientes" && (
          <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
            {bucketsClientesVisibles.map(b => {
              const on = bucketClientes === b.id;
              return (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => setBucketClientes(b.id)}
                  style={{
                    padding: "7px 16px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                    border: `1px solid ${on ? t.accentBlue : t.border}`,
                    background: on ? t.accentBlue : t.bgCard,
                    color: on ? "#fff" : t.textSecondary,
                    transition: "all 0.15s",
                  }}
                >
                  {b.label}
                </button>
              );
            })}
          </div>
        )}
        {vista === "clientes"     && <TablaClientes t={t} isDark={isDark} navigate={navigate} bucket={bucketClientes} onCambiarBucket={setBucketClientes} />}
        {vista === "propietarios" && <TablaContactos tipo="propietario" t={t} isDark={isDark} />}
        {vista === "otros"        && <TablaContactos tipo="contacto"    t={t} isDark={isDark} />}
      </main>
    </div>
  );
}
